import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  const STORAGE_KEY = 'cart';

  // Load cart from sessionStorage on mount (fallback to localStorage for legacy carts)
  useEffect(() => {
    const savedCart = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!savedCart) return;

    try {
      const parsed = JSON.parse(savedCart);
      setCartItems(Array.isArray(parsed) ? parsed : []);

      // Migrate any legacy localStorage cart into session storage for this session.
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.isArray(parsed) ? parsed : []));
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to load cart:', error);
      sessionStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Save cart to sessionStorage whenever it changes (session persistence)
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (plan) => {
    // For subscription plans, we typically only allow one plan at a time
    setCartItems([{ ...plan, quantity: 1, type: 'subscription' }]);
  };

  const removeFromCart = (planId) => {
    setCartItems(cartItems.filter((item) => item.id !== planId));
  };

  const clearCart = () => {
    setCartItems([]);
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
  };

  const getCartTotal = () =>
    cartItems.reduce(
      (total, item) => total + (parseFloat(item.price) || 0) * (item.quantity || 1),
      0
    );

  const getCartCount = () => cartItems.reduce((count, item) => count + (item.quantity || 1), 0);

  const value = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      getCartTotal,
      getCartCount,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartItems]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
